import { useState } from "react";
import { ArrowRight, Loader2, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

// Le richieste vere raccolte parlando con i professionisti: niente recensioni inventate.
const COPY = {
  it: {
    title: "Cosa mi chiedono i professionisti",
    sub: "Ecco le richieste di chi fa il tuo lavoro ogni giorno.",
    asked: [
      {
        quote: "Io non faccio preventivi, ma mi servirebbero le fatture automatiche ogni settimana.",
        who: "Psicologa",
      },
      {
        quote: "A me servono solo le fatture da personalizzare.",
        who: "Consulente finanziario",
      },
    ],
    formTitle: "E a te cosa farebbe risparmiare più tempo?",
    formSub: "Scrivimelo. Le leggo tutte io, e quelle che tornano più spesso le costruisco prima.",
    placeholder: "es. Vorrei che le fatture del mio abbonamento mensile si creassero da sole",
    job: "Che lavoro fai? (facoltativo)",
    email: "Email, se vuoi che ti avvisi quando lo faccio (facoltativo)",
    send: "Manda la richiesta",
    done: "Richiesta arrivata. Grazie davvero.",
    short: "Scrivi qualche parola in più (almeno 10 caratteri).",
    error: "Non è partita. Riprova tra un attimo.",
    limit: "Hai già mandato diverse richieste oggi. A domani!",
    thanksTitle: "Ricevuta.",
    thanksText: "La leggo io personalmente. Se lascio la tua richiesta nella lista pubblica, prima ti chiedo il permesso.",
  },
  en: {
    title: "What professionals ask me",
    sub: "Here are the requests from people doing your job every day.",
    asked: [
      {
        quote: "I don't write quotes, but I'd need invoices generated automatically every week.",
        who: "Psychologist",
      },
      {
        quote: "All I need is invoices I can customise.",
        who: "Financial advisor",
      },
    ],
    formTitle: "And what would save you the most time?",
    formSub: "Tell me. I read every single one, and the ones that come back most often get built first.",
    placeholder: "e.g. I'd like the invoices for my monthly retainer to create themselves",
    job: "What do you do? (optional)",
    email: "Email, if you want me to tell you when it's ready (optional)",
    send: "Send my request",
    done: "Got it. Thank you.",
    short: "Add a few more words (at least 10 characters).",
    error: "It didn't go through. Try again in a moment.",
    limit: "You've already sent several requests today. See you tomorrow!",
    thanksTitle: "Received.",
    thanksText: "I read it personally. If I add your request to the public list, I'll ask your permission first.",
  },
};

export const RequestsWall = () => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const [text, setText] = useState("");
  const [job, setJob] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) return toast.error(c.short);
    setSending(true);
    try {
      await api.post("/public/request", { text, job, email });
      setSent(true);
      toast.success(c.done);
    } catch (err) {
      toast.error(err.response?.status === 429 ? c.limit : c.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className="border-t border-slate-200 px-6 py-20 lg:px-16"
      data-testid="landing-requests"
    >
      <div className="mx-auto grid max-w-5xl gap-14 lg:grid-cols-2 lg:items-start">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight lg:text-4xl">{c.title}</h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-600 md:text-base">
            {c.sub}
          </p>
          <ul className="mt-10 space-y-8">
            {c.asked.map((a) => (
              <li key={a.who} className="border-l-2 border-indigo-500 pl-5">
                <p className="text-base italic leading-relaxed text-slate-900 md:text-lg">
                  “{a.quote}”
                </p>
                <p className="mt-3 text-sm text-slate-500">{a.who}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-flat bg-slate-50 p-8">
          {sent ? (
            <div data-testid="request-success">
              <p className="text-base font-semibold md:text-lg">{c.thanksTitle}</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.thanksText}</p>
            </div>
          ) : (
            <form onSubmit={send}>
              <p className="flex items-center gap-2 text-base font-semibold md:text-lg">
                <MessageSquarePlus className="h-4 w-4 text-indigo-600" /> {c.formTitle}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.formSub}</p>
              <textarea
                data-testid="request-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={c.placeholder}
                rows={3}
                className="mt-5 w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none"
              />
              <input
                data-testid="request-job"
                value={job}
                onChange={(e) => setJob(e.target.value)}
                placeholder={c.job}
                className="mt-3 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none"
              />
              <input
                data-testid="request-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={c.email}
                className="mt-3 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none"
              />
              <Button
                data-testid="request-submit"
                type="submit"
                disabled={sending}
                className="mt-5 w-full gap-2 bg-slate-900 py-6 text-base text-white hover:bg-slate-800"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {c.send}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
